import useCustomFieldsHook from "@/hooks/use-custom-fields.hook.tsx";
import useEmployeeCustomFieldsHook from "@/hooks/use-employee-custom-fields.hook.tsx";
import { CustomTable } from "@/pages/employees/employee/tables/CustomTable.tsx";
import { useAppConfig } from "@/queries/settings/use-app.query.ts";
import { ApiCFTabType, ApiSectionTypes, ApiTableTypes } from "@/types/custom-field.types.ts";
import { ApiEmployee } from "@/types/employees/employee.types.ts";
import { Box, Stack, Title } from "@skailer-com/uikit";
import { type ReactNode, type RefObject } from "react";
import { ApiFeatureType } from "@/types/subscription.types.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { EmployeeFieldPermission } from "@/types/role.types.ts";
import { EmployeePageOverviewTeams } from "@/pages/employees/employee/components/EmployeePageOverviewTeams.tsx";
import { EmployeePageOverviewDirectReports } from "@/pages/employees/employee/components/EmployeePageOverviewDirectReports.tsx";
import { EmployeePageOverviewDepartmentEmployees } from "@/pages/employees/employee/components/EmployeePageOverviewDepartmentEmployees.tsx";
import { EmployeePageOverviewSummary } from "@/pages/employees/employee/components/EmployeePageOverviewSummary.tsx";
import { EmployeeWorkInfoPosition } from "@/pages/employees/employee/components/EmployeeWorkInfoPosition.tsx";
import { EmployeeWorkInfoEmployment } from "@/pages/employees/employee/components/EmployeeWorkInfoEmployment.tsx";
import { EmployeeWorkInfoCompensations } from "@/pages/employees/employee/components/EmployeeWorkInfoCompensation.tsx";
import { EmployeeWorkInfoAdditionalCompensations } from "@/pages/employees/employee/components/EmployeeWorkInfoAdditionalCompensation.tsx";
import { EmployeeRecognitions } from "@/pages/employees/employee/components/EmployeeRecognitions.tsx";

interface EmployeeCustomTabItemsProps {
  employee: ApiEmployee;
  tabType: ApiCFTabType;
  tableScrollBoundaryRef?: RefObject<HTMLElement | null>;
}

export function EmployeeCustomTabItems({
  employee,
  tabType,
  tableScrollBoundaryRef,
}: EmployeeCustomTabItemsProps) {
  const { data: config } = useAppConfig();
  const { renderEmployeeCustomFieldListItem } = useEmployeeCustomFieldsHook();
  const { getSectionTitle } = useCustomFieldsHook();
  const {
    isFeatureEnabled,
    canViewAnyOfFields,
    canEditAnyOfFields,
    canViewCustomTableForEmployee,
    canViewField,
  } = usePermissions();

  const tabItem = config?.ui?.employee_profile?.tabs?.find((item) => item.tab.type === tabType);

  const canViewPosition =
    canViewAnyOfFields(employee, [
      EmployeeFieldPermission.employee_positions_history,
      EmployeeFieldPermission.employee_position_date,
      EmployeeFieldPermission.employee_position_job_title,
      EmployeeFieldPermission.employee_position_job_track,
      EmployeeFieldPermission.employee_position_job_grade,
      EmployeeFieldPermission.employee_position_job_salary,
      EmployeeFieldPermission.employee_position_reporting_to,
      EmployeeFieldPermission.employee_position_department,
      EmployeeFieldPermission.employee_position_division,
      EmployeeFieldPermission.employee_position_location,
      EmployeeFieldPermission.employee_position_comment,
    ]) || canEditAnyOfFields(employee, [EmployeeFieldPermission.manage_employee_position]);

  const canViewEmployment =
    canViewAnyOfFields(employee, [
      EmployeeFieldPermission.employee_employments_history,
      EmployeeFieldPermission.employee_employment_date,
      EmployeeFieldPermission.employee_employment_legal_entity,
      EmployeeFieldPermission.employee_employment_contract,
      EmployeeFieldPermission.employee_employment_employment_type,
      EmployeeFieldPermission.employee_employment_work_pattern,
      EmployeeFieldPermission.employee_employment_probation,
      EmployeeFieldPermission.employee_employment_comment,
      EmployeeFieldPermission.employee_termination_date,
      EmployeeFieldPermission.employee_termination_last_working_date,
      EmployeeFieldPermission.employee_termination_leave_type,
      EmployeeFieldPermission.employee_termination_reason,
      EmployeeFieldPermission.employee_termination_replacement_employee,
      EmployeeFieldPermission.employee_termination_eligible_for_rehire,
      EmployeeFieldPermission.employee_termination_comment,
      EmployeeFieldPermission.employee_termination_changed_by,
    ]) || canEditAnyOfFields(employee, [EmployeeFieldPermission.manage_employee_employment]);

  const canViewCompensation =
    canViewAnyOfFields(employee, [
      EmployeeFieldPermission.employee_compensations_history,
      EmployeeFieldPermission.employee_compensation_date,
      EmployeeFieldPermission.employee_compensation_base_salary_gross,
      EmployeeFieldPermission.employee_compensation_base_salary_net,
      EmployeeFieldPermission.employee_compensation_change_reason,
      EmployeeFieldPermission.employee_compensation_comment,
    ]) || canEditAnyOfFields(employee, [EmployeeFieldPermission.manage_employee_compensation]);

  const canViewAdditionalCompensation = canViewAnyOfFields(employee, [
    EmployeeFieldPermission.employee_additional_compensation,
  ]);

  const canViewRecognitions =
    isFeatureEnabled(ApiFeatureType.recognitions) &&
    canViewField(employee, EmployeeFieldPermission.employee_recognitions);

  if (!tabItem) return null;

  return (
    <>
      {tabItem.items.map((item) => {
        if (item.table) {
          const table = item.table;

          switch (table.table_type) {
            case ApiTableTypes.employee_positions:
              if (!canViewPosition) return null;
              return (
                <div id={`table-${table.id}`} key={`system-table-${table.id}`}>
                  <EmployeeWorkInfoPosition
                    employee={employee}
                    tableScrollBoundaryRef={tableScrollBoundaryRef}
                  />
                </div>
              );
            case ApiTableTypes.employee_employments:
              if (!canViewEmployment) return null;
              return (
                <div id={`table-${table.id}`} key={`system-table-${table.id}`}>
                  <EmployeeWorkInfoEmployment
                    employee={employee}
                    tableScrollBoundaryRef={tableScrollBoundaryRef}
                  />
                </div>
              );
            case ApiTableTypes.employee_terminations:
              return null;
            case ApiTableTypes.employee_compensations:
              if (!canViewCompensation) return null;
              return (
                <div id={`table-${table.id}`} key={`system-table-${table.id}`}>
                  <EmployeeWorkInfoCompensations
                    employee={employee}
                    tableScrollBoundaryRef={tableScrollBoundaryRef}
                  />
                </div>
              );
            case ApiTableTypes.employee_additional_compensations:
              if (!canViewAdditionalCompensation) return null;
              return (
                <div id={`table-${table.id}`} key={`system-table-${table.id}`}>
                  <EmployeeWorkInfoAdditionalCompensations
                    employee={employee}
                    tableScrollBoundaryRef={tableScrollBoundaryRef}
                  />
                </div>
              );
            case ApiTableTypes.custom:
            default:
              if (!isFeatureEnabled(ApiFeatureType.custom_fields)) return null;
              if (!canViewCustomTableForEmployee(employee, table.id)) return null;
              return (
                <div id={`table-${table.id}`} key={`custom-table-${table.id}`}>
                  <CustomTable
                    table={table}
                    employee={employee}
                    tableScrollBoundaryRef={tableScrollBoundaryRef}
                  />
                </div>
              );
          }
        }

        if (item.section) {
          const { section, fields } = item.section;
          const renderedFields: { fieldId: number; node: ReactNode }[] = [];

          fields.forEach((field) => {
            const rendered = renderEmployeeCustomFieldListItem(field, employee);
            if (rendered) renderedFields.push({ fieldId: field.id, node: rendered });
          });

          if (section.section_type == ApiSectionTypes.employee_overview_teams) {
            return (
              <div id={`section-${section.id}`} key={`system-section-${section.id}`}>
                <EmployeePageOverviewTeams employee={employee} />
              </div>
            );
          }

          if (section.section_type == ApiSectionTypes.employee_overview_summary) {
            return (
              <div id={`section-${section.id}`} key={`system-section-${section.id}`}>
                <EmployeePageOverviewSummary employee={employee} />
              </div>
            );
          }

          if (section.section_type == ApiSectionTypes.employee_overview_direct_reports) {
            return (
              <div id={`section-${section.id}`} key={`system-section-${section.id}`}>
                <EmployeePageOverviewDirectReports employee={employee} />
              </div>
            );
          }

          if (section.section_type == ApiSectionTypes.employee_overview_department_employees) {
            return (
              <div id={`section-${section.id}`} key={`system-section-${section.id}`}>
                <EmployeePageOverviewDepartmentEmployees employee={employee} />
              </div>
            );
          }

          if (section.section_type == ApiSectionTypes.employee_overview_recognitions) {
            if (!canViewRecognitions) return null;
            return (
              <div id="section-recognitions" key={`system-section-${section.id}`}>
                <EmployeeRecognitions employee={employee} />
              </div>
            );
          }

          if (renderedFields.length === 0) return null;

          if (isFeatureEnabled(ApiFeatureType.custom_fields)) {
            return (
              <Stack
                id={`section-${section.id}`}
                key={`custom-section-${section.id}`}
                direction={"vertical"}
                gap={"md"}
              >
                <Title size={"lg"} header={getSectionTitle(section)} />
                <Box variant={"border"}>
                  {renderedFields.map(({ fieldId, node }, fieldIndex) => (
                    <div
                      id={`field-${fieldId}`}
                      key={`custom-section-${section.id}-field-${fieldIndex}`}
                    >
                      {node}
                    </div>
                  ))}
                </Box>
              </Stack>
            );
          }
        }

        return null;
      })}
    </>
  );
}
